import { ListBulletIcon, Squares2X2Icon } from "@heroicons/react/24/outline";
import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom"; // 라우트 변경 감지
import { getData } from "../../apis";
import { API } from "../../apis/config";
import CartButton from "../../components/CartButton";
import LoadingScreen from "../../components/LoadingScreen";
import ScrollToTopButton from "../../components/ScrollToTopButton";
import SearchProductInput from "../../components/SearchProductInput";
import { ParcelProduct } from "../../interfaces/ParcelProduct";
import { RootState } from "../../stores";
import { set, setLike } from "../../stores/slices/parcelSlice";
import {
  changeHeaderFilter,
  convertFilterIcon,
} from "../../utils/headerFilter";
import {
  addItemToLocalStorageArray,
  loadArrayFromLocalStorage,
  localStorageKeys,
  removeItemFromLocalStorageArray,
} from "../../utils/localStorage";
import { matchesProductNameSearch } from "../../utils/koreanSearch";
import { parcelCdnImageUrl } from "../../utils/parcelCdnImageUrl";

type ViewMode = "list" | "grid";

function SearchProduct() {
  const [searchKeyword, setSearchKeyword] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [viewMode, setViewMode] = useState<ViewMode>("grid");

  const [sortedData, setSortedData] = useState<ParcelProduct[]>([]);
  const [headerFilter] = useState<{ [key: string]: number }>({});

  const dispatch = useDispatch();
  const location = useLocation(); // 라우트 변경 감지

  const productSliceData = useSelector(
    (state: RootState) => state.parcelProduct.searchRows,
  );

  const Header = {
    Name: "name",
    Price: "price",
    Save: "save",
  } as const;

  const handleLikeClick = (row: ParcelProduct): ParcelProduct => {
    const newRow = { ...row, isLike: !row.isLike, quantity: 1 };

    if (row.isLike) {
      removeItemFromLocalStorageArray(localStorageKeys.cartProduct, newRow.id);
    } else {
      addItemToLocalStorageArray(localStorageKeys.cartProduct, {
        id: newRow.id,
        isLike: newRow.isLike,
        quantity: newRow.quantity,
      });
    }
    dispatch(setLike(newRow.id));

    // 로컬 상태 업데이트
    const updatedList = sortedData.map((item) =>
      item.id === newRow.id ? { ...item, isLike: !row.isLike } : item,
    );
    setSortedData(updatedList);

    return newRow;
  };

  const clickHeader = (
    key: string,
    compareFn: (a: ParcelProduct, b: ParcelProduct) => number,
  ) => {
    changeHeaderFilter(
      headerFilter,
      setSortedData,
      key,
      compareFn,
      productSliceData,
    );
  };

  const createFilterIcon = (key: string): string => {
    return convertFilterIcon(headerFilter, key);
  };

  // localStorage 동기화
  const syncCartWithLocalStorage = async () => {
    const localCart = loadArrayFromLocalStorage(localStorageKeys.cartProduct);

    await getData<ParcelProduct[]>(API.PARCEL.PRODUCTS)
      .then((res) => {
        const mergedData = res.result.map((product) => {
          const match = localCart.find((item) => item.id === product.id);
          return {
            ...product,
            isLike: !!match,
            quantity: match?.quantity || 1,
          };
        });

        dispatch(set(mergedData.filter((value) => value.searchable)));
        setSortedData(mergedData);
      })
      .finally(() => setIsLoading(false));
  };

  // 1) 페이지 진입 + 라우트 변경 시 동기화
  useEffect(() => {
    syncCartWithLocalStorage();
  }, [location.pathname]);

  // 2) 다른 탭 localStorage 변경 감지
  useEffect(() => {
    const handleStorageChange = (event: StorageEvent) => {
      if (event.key === localStorageKeys.cartProduct) {
        syncCartWithLocalStorage();
      }
    };
    window.addEventListener("storage", handleStorageChange);
    return () => {
      window.removeEventListener("storage", handleStorageChange);
    };
  }, []);

  // 3) Redux → sortedData 초기 세팅
  useEffect(() => {
    if (sortedData.length === 0) {
      setSortedData(productSliceData);
    }
  }, [productSliceData]);

  const filteredItems = useMemo(
    () =>
      sortedData.filter((item) =>
        matchesProductNameSearch(item.name, searchKeyword),
      ),
    [sortedData, searchKeyword],
  );

  return (
    <div>
      {isLoading ? (
        <LoadingScreen />
      ) : (
        <div className="px-3 sm:px-4 md:mx-20">
          <ScrollToTopButton />

          <SearchProductInput
            searchKeyword={searchKeyword}
            setSearchKeyword={setSearchKeyword}
          />
          <div className="flex flex-wrap gap-3 items-center justify-between mb-4 gap-y-2">
            <div className="flex flex-wrap gap-2 items-center">
              <span className="text-sm font-medium text-slate-700">정렬</span>
              <button
                type="button"
                className="rounded-md border border-slate-300 bg-white px-3 py-1.5 text-sm text-slate-700 hover:bg-slate-50"
                onClick={() =>
                  clickHeader(Header.Name, (a, b) =>
                    a.name.localeCompare(b.name, "ko"),
                  )
                }
              >
                상품명{createFilterIcon(Header.Name)}
              </button>
              <button
                type="button"
                className="rounded-md border border-slate-300 bg-white px-3 py-1.5 text-sm text-slate-700 hover:bg-slate-50"
                onClick={() =>
                  clickHeader(Header.Price, (a, b) => b.price - a.price)
                }
              >
                가격{createFilterIcon(Header.Price)}
              </button>
              <button
                type="button"
                className="rounded-md border border-slate-300 bg-white px-3 py-1.5 text-sm text-slate-700 hover:bg-slate-50"
                onClick={() =>
                  clickHeader(Header.Save, (a, b) =>
                    a.isLike === b.isLike ? 0 : a.isLike ? -1 : 1,
                  )
                }
              >
                담기{createFilterIcon(Header.Save)}
              </button>
            </div>

            <div
              className="flex items-center gap-2"
              role="group"
              aria-label="보기 방식"
            >
              <span className="text-sm font-medium text-slate-700 whitespace-nowrap">
                보기
              </span>
              <div className="inline-flex rounded-lg border border-slate-300 p-0.5 bg-slate-100">
                <button
                  type="button"
                  onClick={() => setViewMode("list")}
                  aria-pressed={viewMode === "list"}
                  className={`inline-flex items-center gap-1 rounded-md px-2.5 py-1.5 text-sm font-medium transition-colors ${
                    viewMode === "list"
                      ? "bg-white text-slate-900 shadow-sm"
                      : "text-slate-600 hover:text-slate-900"
                  }`}
                >
                  <ListBulletIcon className="h-4 w-4 shrink-0" aria-hidden />
                  리스트
                </button>
                <button
                  type="button"
                  onClick={() => setViewMode("grid")}
                  aria-pressed={viewMode === "grid"}
                  className={`inline-flex items-center gap-1 rounded-md px-2.5 py-1.5 text-sm font-medium transition-colors ${
                    viewMode === "grid"
                      ? "bg-white text-slate-900 shadow-sm"
                      : "text-slate-600 hover:text-slate-900"
                  }`}
                >
                  <Squares2X2Icon className="h-4 w-4 shrink-0" aria-hidden />
                  썸네일
                </button>
              </div>
            </div>
          </div>

          {viewMode === "list" ? (
            <table className="my-4 w-full bg-white border border-slate-200 rounded-lg overflow-hidden">
              <thead className="bg-gray-200">
                <tr>
                  <th
                    className="w-1/3 px-4 py-2 text-center text-gray-600 cursor-pointer"
                    onClick={() =>
                      clickHeader(Header.Name, (a, b) =>
                        a.name.localeCompare(b.name, "ko"),
                      )
                    }
                  >
                    상품명{createFilterIcon(Header.Name)}
                  </th>
                  <th
                    className="w-3/12 px-4 py-2 text-center text-gray-600 cursor-pointer"
                    onClick={() =>
                      clickHeader(Header.Price, (a, b) => b.price - a.price)
                    }
                  >
                    가격{createFilterIcon(Header.Price)}
                  </th>
                  <th
                    className="w-2/12 px-4 py-2 text-center text-gray-600 cursor-pointer"
                    onClick={() =>
                      clickHeader(Header.Save, (a, b) =>
                        a.isLike === b.isLike ? 0 : a.isLike ? -1 : 1,
                      )
                    }
                  >
                    담기{createFilterIcon(Header.Save)}
                  </th>
                </tr>
              </thead>
              <tbody>
                {filteredItems.map((item) => (
                  <tr
                    onClick={() => handleLikeClick(item)}
                    key={item.id}
                    className={`cursor-pointer border-t border-slate-100 ${
                      item.note ? "underline" : ""
                    }`}
                  >
                    <td className="px-4 py-2 text-sm md:text-base">
                      {item.name}
                    </td>
                    <td className="px-4 py-2">{item.price.toLocaleString()}</td>
                    <td className="px-4 py-2">
                      <CartButton onClick={() => {}} isInCart={item.isLike} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 xl:grid-cols-7 gap-3 sm:gap-4 my-4">
              {filteredItems.map((item) => (
                <div
                  key={item.id}
                  tabIndex={0}
                  onClick={() => handleLikeClick(item)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      handleLikeClick(item);
                    }
                  }}
                  className={`flex flex-col rounded-lg border border-slate-200 bg-white shadow-sm overflow-hidden cursor-pointer ${
                    item.note ? "underline" : ""
                  }`}
                >
                  <div className="relative aspect-square w-full bg-slate-100 overflow-hidden shrink-0">
                    {item.thumbnailUrl ? (
                      <img
                        src={parcelCdnImageUrl(item.thumbnailUrl)}
                        alt=""
                        className="h-full w-full object-cover"
                        loading="lazy"
                        decoding="async"
                      />
                    ) : (
                      <div className="flex h-full w-full items-center justify-center px-2 text-center text-xs text-slate-400">
                        이미지 준비중
                      </div>
                    )}
                    <div
                      className="absolute bottom-2 right-2 z-10 flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-white/95 shadow-md ring-1 ring-black/10"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <CartButton
                        className="inline-flex items-center justify-center rounded-full p-0 leading-none"
                        onClick={() => handleLikeClick(item)}
                        isInCart={item.isLike}
                      />
                    </div>
                  </div>
                  <div className="flex flex-1 flex-col gap-1 p-2 min-h-0">
                    <h3 className="text-xs sm:text-sm font-medium text-slate-900 line-clamp-2 leading-snug">
                      {item.name}
                    </h3>
                    <p className="text-sm font-semibold text-slate-800">
                      {item.price.toLocaleString()}원
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default SearchProduct;
